/**
 * Landing Page Facade (100% Shared)
 * 
 * Composes all necessary facades for the landing page.
 * Uses dependency injection pattern to remain platform-agnostic.
 * 
 * This facade is 100% shared between web and plugin.
 * Platform-specific implementations (navigation, auth) are injected.
 * 
 * Usage (Web):
 * ```tsx
 * const navigation = useWebNavigation();
 * const auth = useAuthDialog();
 * const facade = useLandingPageFacade({ navigation, auth, showToast });
 * ```
 * 
 * Usage (Plugin):
 * ```tsx
 * const navigation = usePluginNavigation();
 * const auth = usePairingCodeAuth();
 * const facade = useLandingPageFacade({ navigation, auth, showToast });
 * ```
 * 
 * @module useLandingPageFacade
 * @category Facades/Pages
 */

import { useMemo, useEffect, useState } from 'react';
import React from 'react';
import { useAppSelector, useAppDispatch } from '../../../store/hooks';
import { selectIsAppLoading, selectOnLandingFirstTime, setOnLandingFirstTime } from '../../../store/slices/appSlice';
import { selectConversationLlmFamily } from '../../../store/slices/conversationSlice';
import { useHeaderConfig } from '../../useHeaderConfig';
import { useConversationFacade } from '../shared/useConversationFacade';
import { useProjectFacade } from '../shared/useProjectFacade';
import { useSidebarConfig, type SidebarConfig } from '../shared/useSidebarConfig';
import type { NavigationFacade, AuthFacade, ShowToastFn } from '../shared/types';
import { getUserProfile } from '../../../store/slices/appSlice';

export interface LandingPageFacadeParams {
  /**
   * Platform-specific navigation implementation
   */
  navigation: NavigationFacade;

  /**
   * Platform-specific auth implementation
   */
  auth: AuthFacade;

  /**
   * Platform-specific toast notification function
   */
  showToast: ShowToastFn;
}

export interface LandingPageFacade {
  // ========== State ==========
  state: {
    isLoggedIn: boolean;
    isAppLoading: boolean;
    myProjects: any[];  // From project facade
    isLoadingProjects: boolean;  // From project facade
    llmFamily?: string;
    onLandingFirstTime: boolean;  // True if user is new (from visitors/check API)
    planDetails?: any;  // User's plan details from Redux
  };

  // ========== User Lands Dialog ==========
  userLandsDialog: {
    isOpen: boolean;
    onClose: () => void;
    planDetails?: any[];
  };

  // ========== Header ==========
  header: ReturnType<typeof useHeaderConfig>;

  // ========== Navigation ==========
  navigation: NavigationFacade;

  // ========== Conversation ==========
  conversation: ReturnType<typeof useConversationFacade>;

  // ========== Project ==========
  project: ReturnType<typeof useProjectFacade>;

  // ========== Auth ==========
  auth: AuthFacade;

  // ========== Sidebar ==========
  sidebar: SidebarConfig;
}

/**
 * Landing Page Facade Hook
 * 
 * Composes all necessary facades and state for the landing page.
 * 100% shared between web and plugin through dependency injection.
 */
export function useLandingPageFacade({
  navigation,
  auth,
  showToast,
}: LandingPageFacadeParams): LandingPageFacade {
  // ========== Global State ==========
  const dispatch = useAppDispatch();
  const isAppLoading = useAppSelector(selectIsAppLoading);
  const llmFamily = useAppSelector(selectConversationLlmFamily);
  const onLandingFirstTime = useAppSelector(selectOnLandingFirstTime);

  // ========== User Lands Dialog Handler ==========
  const handleCloseUserLandsDialog = React.useCallback(() => {
    dispatch(setOnLandingFirstTime(false));
  }, [dispatch]);

  // ========== Reusable Facades ==========
  const conversation = useConversationFacade();
  const project = useProjectFacade();

  // ========== Header Config ==========
  const headerConfig = useHeaderConfig({
    onNavigateToProfile: navigation.toProfile,
    onNavigateToBilling: navigation.toBilling,
    onNavigateToAppSettings: navigation.toAppSettings,
    onNavigateToReport: navigation.toReportProblem,
    onLoginClick: auth.openLogin,
    onRegisterClick: auth.openRegister,
    showToast,
    currentPath: '/',
  });

  // ========== Sidebar Config ==========
  const sidebar = useSidebarConfig({
    pageType: 'standard',
    isLoggedIn: headerConfig.isLoggedIn,
    onResetConversation: conversation.reset,
    onNavigateToLanding: navigation.toLanding,
    onNavigateToProjectsList: navigation.toProjectsList,
    onModeChange: () => { }
  });

  // ========== User Data from Redux ==========
  const planDetails = useAppSelector(state => state.app.planDetails);
  // Try to get accessToken from auth facade, fallback to empty string
  const accessToken = (auth && (auth as any).accessToken) ? (auth as any).accessToken : '';
  useEffect(() => {
    if (accessToken) {
      dispatch(getUserProfile(accessToken));
    }
  }, [accessToken, dispatch]);

  // ========== Filter Active Projects for Landing Page ==========
  // Landing page should only show active projects (not archived)
  const activeProjects = useMemo(() => {
    return project.projects.filter((p: any) => {
      // Check various possible field names for active status
      const isActive = p.isActive ?? p.is_active ?? (p.status !== 'archived');
      return isActive !== false;
    });
  }, [project.projects]);

  // ========== Return Composed Facade ==========
  return useMemo(
    () => ({
      state: {
        isLoggedIn: headerConfig.isLoggedIn,
        isAppLoading,
        myProjects: activeProjects,
        isLoadingProjects: project.isLoading,
        llmFamily,
        onLandingFirstTime,
        planDetails,
      },
      userLandsDialog: {
        isOpen: false, // onLandingFirstTime
        onClose: handleCloseUserLandsDialog,
        planDetails,
      },
      header: headerConfig,
      navigation,
      conversation,
      project,
      auth,
      sidebar,
    }),
    [isAppLoading, headerConfig, navigation, conversation, project, auth, sidebar, llmFamily, onLandingFirstTime, handleCloseUserLandsDialog, planDetails, activeProjects]
  );
}
